import * as THREE from 'three';
import { getFullUrl } from '../config';

/**
 * Texture optimization utilities for device-specific performance
 */
class TextureOptimizer {
  constructor() {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.isLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
    this.maxTextureSize = this.getOptimalTextureSize();
    this.compressionEnabled = this.shouldEnableCompression();
  }

  /**
   * Determine optimal texture size based on device capabilities
   */
  getOptimalTextureSize() {
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) {
      return 512; // Very low memory devices
    } else if (this.isMobile || this.isLowMemory) {
      return 1024; // Mobile and low memory devices
    } else {
      return 2048; // Desktop and high memory devices
    }
  }

  /**
   * Check if texture compression should be enabled
   */
  shouldEnableCompression() {
    return this.isMobile || this.isLowMemory || (performance.memory && 
           performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit > 0.6);
  }

  /**
   * Optimize texture based on device capabilities
   */
  optimizeTexture(texture, item) {
    if (!texture || !texture.image) return texture;

    try {
      // Set appropriate filtering based on device
      if (this.isMobile || this.isLowMemory) {
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
      } else {
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = true;
      }

      // Resize texture if too large for device
      if (texture.image.width > this.maxTextureSize || texture.image.height > this.maxTextureSize) {
        console.log(`📐 Resizing texture for ${item.name}: ${texture.image.width}x${texture.image.height} -> ${this.maxTextureSize}px max`);
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions maintaining aspect ratio
        const aspectRatio = texture.image.width / texture.image.height;
        let newWidth, newHeight;
        
        if (texture.image.width > texture.image.height) {
          newWidth = Math.min(texture.image.width, this.maxTextureSize);
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.min(texture.image.height, this.maxTextureSize);
          newWidth = newHeight * aspectRatio;
        }
        
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Use high quality to preserve artwork colors
        let quality = 0.95; // Increased base quality
        if (this.isMobile) quality = 0.9;  // Higher mobile quality
        if (this.isLowMemory) quality = 0.85; // Higher low-memory quality
        
        // Use high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(texture.image, 0, 0, newWidth, newHeight);
        
        // Create new texture from resized canvas
        const resizedTexture = new THREE.CanvasTexture(canvas);
        resizedTexture.colorSpace = THREE.SRGBColorSpace; // Ensure correct color space
        resizedTexture.minFilter = texture.minFilter;
        resizedTexture.magFilter = texture.magFilter;
        resizedTexture.generateMipmaps = texture.generateMipmaps;
        resizedTexture.needsUpdate = true;
        
        // Clean up original texture
        if (texture.dispose) texture.dispose();
        
        console.log(`✅ Texture optimized: ${newWidth}x${newHeight} (quality: ${quality})`);
        return resizedTexture;
      }

      texture.needsUpdate = true;
      return texture;
    } catch (error) {
      console.warn('Texture optimization failed:', error);
      return texture; // Return original if optimization fails
    }
  }

  /**
   * Get memory-efficient material settings
   */
  getOptimizedMaterialSettings() {
    const settings = {};
    
    if (this.isMobile || this.isLowMemory) {
      // Preserve color vibrancy while optimizing performance
      settings.roughness = 0.2; // Reduced from 0.7 to preserve colors
      settings.metalness = 0.0;  // Reduced from 0.1 to prevent metallic desaturation
      // Disable some expensive features
      settings.normalMap = null;
      settings.bumpMap = null;
    }
    
    return settings;
  }
}

// Global texture optimizer instance
const textureOptimizer = new TextureOptimizer();

/**
 * Handles artwork creation, placement, and 3D object management
 */
export class ArtworkManager {
  constructor(scene, loadingManager = null) {
    this.scene = scene;
    this.loadingManager = loadingManager;
    this.interactiveObjects = [];
  }

  /**
   * Add artwork to the scene
   * @param {Object} item - Item data
   * @param {Object} placement - Placement data
   * @param {Object} placementSystem - Placement system (optional)
   * @param {THREE.Object3D} targetContainer - Container for artwork (optional, uses scene if not provided)
   */
  async addArtwork(item, placement, placementSystem = null, targetContainer = null) {
    console.log(`🖼️ Adding artwork: ${item.name} (ID: ${item.id}) to showroom ${placement.showroomId}`);
    
    // Determine which container to use for this artwork
    const artworkContainer = targetContainer || this.scene;
    const isUsingContainer = !!targetContainer;
    
    // Adding artwork to container
    
    const { positionX, positionY, positionZ, rotationY } = placement;

    // Check if we should use the universal placement system
    let finalPosition = new THREE.Vector3(positionX, positionY, positionZ);
    let finalRotation = rotationY;

    if (placementSystem && placement.surfaceId && placement.gridX !== undefined && placement.gridY !== undefined) {
      // Use new placement system
      console.log(`Using Universal Placement System for item ${item.id} on surface ${placement.surfaceId} at grid (${placement.gridX}, ${placement.gridY})`);

      // Create a temporary mesh to calculate placement
      const tempMesh = new THREE.Group();
      const success = placementSystem.placeObject(tempMesh, placement.surfaceId, placement.gridX, placement.gridY, {
        offset: 0.01,
        rotation: rotationY
      });

      if (success) {
        finalPosition = tempMesh.position.clone();
        finalRotation = tempMesh.rotation.y;
        console.log(`Placed using Universal Placement System at`, finalPosition);
      } else {
        console.warn('Universal Placement System failed, falling back to coordinates');
      }
    }

    // LOGIC: Determine rendering based on surface placement
    // Floor placement = pedestal, Wall placement = no pedestal
    const isFloorPlacement = this.isFloorSurface(placement, placementSystem);
    
    const group = isFloorPlacement ? 
      await this.createFloorItemGroup(item) : 
      await this.createWallItemGroup(item);
      
    group.position.copy(finalPosition);
    group.rotation.y = finalRotation;
    group.userData = { 
      item, 
      placement,
      itemId: item.id, // Add for easier identification
      showroomId: placement.showroomId, // Track which room this belongs to
      artworkType: isFloorPlacement ? 'floor' : 'wall'
    };
    
    // Add unique name for debugging
    group.name = `artwork_${item.id}_showroom_${placement.showroomId}`;

    artworkContainer.add(group);
    this.interactiveObjects.push(group);
    
    // Artwork added
  }

  /**
   * Determine if placement is on floor surface
   */
  isFloorSurface(placement, placementSystem) {
    // If no placement system, use old logic based on Y position
    if (!placementSystem || !placement.surfaceId) {
      return placement.positionY <= 0.1; // Items near ground are floor items
    }
    
    // Use placement system to check surface type
    const surface = placementSystem.getSurface(placement.surfaceId);
    return surface && surface.type === 'floor';
  }

  /**
   * Create floor item group (with pedestal) - handles both 3D models and images
   */
  async createFloorItemGroup(item) {
    const group = new THREE.Group();

    // Always add pedestal for floor items
    const pedestalGroup = this.createPedestal();
    group.add(pedestalGroup);

    // Add the actual item content (3D model or image) - await completion
    const contentGroup = await this.createItemContent(item);
    contentGroup.position.y = 1.2; // Position on top of raised pedestal (0.4 + 0.4 + clearance)
    group.add(contentGroup);

    return group;
  }

  /**
   * Create wall item group (no pedestal) - handles both 3D models and images
   */
  async createWallItemGroup(item) {
    const group = new THREE.Group();
    
    // Just the content, no pedestal - await completion
    const contentGroup = await this.createItemContent(item);
    group.add(contentGroup);

    return group;
  }

  /**
   * Create pedestal (shared by floor items)
   */
  createPedestal() {
    const pedestalGroup = new THREE.Group();

    // Load texture for entire pedestal
    const textureLoader = new THREE.TextureLoader();
    const pedestalTexture = textureLoader.load('/textures/pedestal-top.jpg');
    pedestalTexture.wrapS = THREE.RepeatWrapping;
    pedestalTexture.wrapT = THREE.RepeatWrapping;

    // Clone texture for different parts
    const pedestalTextureTop = pedestalTexture.clone();
    const pedestalTextureBottom = pedestalTexture.clone();
    const pedestalTextureSide = pedestalTexture.clone();
    const baseTexture = pedestalTexture.clone();

    pedestalTextureTop.needsUpdate = true;
    pedestalTextureBottom.needsUpdate = true;
    pedestalTextureSide.needsUpdate = true;
    baseTexture.needsUpdate = true;

    // Main pedestal - fully textured
    const pedestalGeometry = new THREE.CylinderGeometry(0.35, 0.4, 0.8, 24);

    // Apply texture to all parts
    const pedestalMaterials = [
      // Top cap - with texture
      new THREE.MeshStandardMaterial({
        map: pedestalTextureTop,
        roughness: 0.3,
        metalness: 0.1
      }),
      // Bottom cap - with texture
      new THREE.MeshStandardMaterial({
        map: pedestalTextureBottom,
        roughness: 0.3,
        metalness: 0.1
      }),
      // Side - with texture
      new THREE.MeshStandardMaterial({
        map: pedestalTextureSide,
        roughness: 0.3,
        metalness: 0.1
      })
    ];

    const pedestal = new THREE.Mesh(pedestalGeometry, pedestalMaterials);
    pedestal.position.y = 0.4; // Raised higher
    pedestal.castShadow = false;
    pedestal.receiveShadow = true;
    pedestalGroup.add(pedestal);

    // Base - with texture
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.45, 0.08, 24),
      new THREE.MeshStandardMaterial({
        map: baseTexture,
        roughness: 0.2,
        metalness: 0.15
      })
    );
    base.position.y = 0.04;
    base.castShadow = false;
    base.receiveShadow = true;
    pedestalGroup.add(base);

    return pedestalGroup;
  }

  /**
   * Create item content - simple file extension check
   */
  async createItemContent(item) {
    const group = new THREE.Group();
    
    if (!item.url) {
      console.warn(`No file URL found for item ${item.name}`);
      return group;
    }
    
    // Simple check: if URL ends with .glb or .gltf, it's a 3D model, otherwise it's an image
    if (item.url.endsWith('.glb') || item.url.endsWith('.gltf')) {
      // 3D model file - await the loading to ensure completion
      await this.load3DModel(item.url, group, item.name);
    } else {
      // Image file - create flat surface with texture (synchronous for now)
      this.loadImageContent(item.url, item, group);
    }
    
    return group;
  }

  /**
   * Load image content as flat surface
   */
  loadImageContent(url, item, group) {
    // Create material with neutral settings to preserve original colors
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff, // White base color - neutral multiplier for texture
      roughness: 0.1,  // Very smooth for vibrant colors
      metalness: 0.0   // No metallic properties
    });
    
    if (url) {
      // Track texture loading operation
      const loadingOperationId = this.loadingManager?.startLoading(`Artwork texture: ${item.name}`);
      
      const textureLoader = new THREE.TextureLoader();
      const texture = textureLoader.load(
        getFullUrl(url),
        // onLoad callback
        (loadedTexture) => {
          console.log(`Texture loaded successfully for item: ${item.name}`);
          
          // Skip texture optimization for artwork to preserve original colors
          loadedTexture.colorSpace = THREE.SRGBColorSpace;
          loadedTexture.needsUpdate = true;
          
          material.needsUpdate = true;
          if (loadingOperationId) {
            this.loadingManager?.finishLoading(loadingOperationId);
          }
        },
        // onProgress callback
        undefined,
        // onError callback
        (error) => {
          console.warn(`Failed to load texture for item: ${item.name}, URL: ${url}. Using fallback color.`);
          material.map = null;
          material.color.setHex(0x888888);
          material.needsUpdate = true;
          if (loadingOperationId) {
            this.loadingManager?.finishLoading(loadingOperationId);
          }
        }
      );
      material.map = texture;
    }

    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(item.width, item.height, item.depth),
      material
    );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  /**
   * Create a 3D item group (pedestal + object) - LEGACY METHOD
   */
  create3DItemGroup(item) {
    const group = new THREE.Group();

    // Create elegant pedestal with base
    const pedestalGroup = new THREE.Group();
    
    // Main pedestal - smaller and more elegant
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.4, 0.6, 24),
      new THREE.MeshStandardMaterial({ 
        color: 0xf5f5f5, // Light marble color
        roughness: 0.3,
        metalness: 0.1
      })
    );
    pedestal.position.y = 0.3;
    pedestal.castShadow = false;
    pedestal.receiveShadow = true;
    pedestalGroup.add(pedestal);
    
    // Elegant base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.45, 0.08, 24),
      new THREE.MeshStandardMaterial({ 
        color: 0xe8e8e8, // Slightly darker base
        roughness: 0.2,
        metalness: 0.15
      })
    );
    base.position.y = 0.04;
    base.castShadow = false;
    base.receiveShadow = true;
    pedestalGroup.add(base);
    
    group.add(pedestalGroup);

    // Load 3D Model using dynamic import - LEGACY VERSION
    if (item.url) {
      this.load3DModel(item.url, group, item.name);
    } else {
      // Fallback vase shape for items without files
      const vase = this.createFallbackVase();
      group.add(vase);
    }

    return group;
  }

  /**
   * Create a painting group (frame + canvas)
   */
  createPaintingGroup(item) {
    const group = new THREE.Group();

    // Just the painting surface - NO FRAMES!
    const paintingMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff, // White base color - neutral multiplier for texture
      roughness: 0.1,  // Very smooth for vibrant colors
      metalness: 0.0   // No metallic properties
    });
    
    if (item.url) {
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(getFullUrl(item.url), (loadedTexture) => {
        // Skip optimization to preserve original colors
        loadedTexture.colorSpace = THREE.SRGBColorSpace;
        loadedTexture.needsUpdate = true;
        paintingMaterial.map = loadedTexture;
        paintingMaterial.needsUpdate = true;
      });
    }

    const painting = new THREE.Mesh(
      new THREE.BoxGeometry(item.width, item.height, item.depth),
      paintingMaterial
    );
    painting.castShadow = true;
    painting.receiveShadow = true;
    group.add(painting);

    return group;
  }

  /**
   * Create a floor-standing painting group (painting + frame + pedestal)
   */
  createFloorPaintingGroup(item) {
    const group = new THREE.Group();

    // Create elegant pedestal base
    const pedestalGroup = new THREE.Group();
    
    // Main pedestal - smaller and more elegant
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.4, 0.6, 24),
      new THREE.MeshStandardMaterial({ 
        color: 0xf5f5f5, // Light marble color
        roughness: 0.3,
        metalness: 0.1
      })
    );
    pedestal.position.y = 0.3;
    pedestal.castShadow = false;
    pedestal.receiveShadow = true;
    pedestalGroup.add(pedestal);
    
    // Elegant base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.45, 0.08, 24),
      new THREE.MeshStandardMaterial({ 
        color: 0xe8e8e8, // Slightly darker base
        roughness: 0.2,
        metalness: 0.15
      })
    );
    base.position.y = 0.04;
    base.castShadow = false;
    base.receiveShadow = true;
    pedestalGroup.add(base);
    
    group.add(pedestalGroup);

    // Create the framed painting, positioned above the pedestal
    const paintingGroup = this.createPaintingGroup(item);
    paintingGroup.position.y = 0.6 + item.height/2; // Position above pedestal
    group.add(paintingGroup);

    return group;
  }


  /**
   * Place an item using the Universal Placement System
   */
  placeItemOnSurface(item, surfaceId, gridX, gridY, options = {}, placementSystem) {
    if (!placementSystem) {
      console.error('Universal Placement System not initialized');
      return null;
    }

    // Create the artwork group
    let group;
    if (item.modelUrl) {
      group = this.create3DItemGroup(item);
    } else {
      group = this.createPaintingGroup(item);
    }

    // Use placement system to position the object
    const success = placementSystem.placeObject(group, surfaceId, gridX, gridY, options);

    if (success) {
      group.userData = {
        item,
        placement: { surfaceId, gridX, gridY, ...options }
      };

      this.scene.add(group);
      this.interactiveObjects.push(group);

      console.log(`Successfully placed item ${item.id} on surface ${surfaceId} at grid (${gridX}, ${gridY})`);
      return group;
    } else {
      console.error(`Failed to place item ${item.id} on surface ${surfaceId}`);
      return null;
    }
  }

  /**
   * Load a 3D model for an item
   */
  async load3DModel(url, group, itemName = 'Unknown') {
    // Track 3D model loading operation
    const loadingOperationId = this.loadingManager?.startLoading(`3D model: ${itemName}`);
    
    try {
      console.log(`Loading 3D model for: ${itemName} from URL:`, getFullUrl(url));
      const gltf = await this.loadGLTFModel(url);
      const model = gltf.scene;
      console.log(`3D model loaded successfully for: ${itemName}`);

      // Position and setup model for items
      model.position.y = 0; // Position relative to group (pedestal height handled by parent)
      model.castShadow = false;
      model.receiveShadow = true;
      model.userData.isRotating = true;

      // Auto-scale model based on item dimensions
      const bbox = new THREE.Box3().setFromObject(model);
      const size = bbox.getSize(new THREE.Vector3());
      const maxDimension = Math.max(size.x, size.y, size.z);

      // Scale to fit within reasonable size (max 1 unit)
      if (maxDimension > 0) {
        const targetSize = 0.8; // Target max dimension
        const scale = targetSize / maxDimension;
        model.scale.setScalar(scale);
      }

      group.add(model);
    } catch (error) {
      console.error('Error loading 3D model from URL:', getFullUrl(url), error);
      // Fallback to basic vase shape for items
      const vase = this.createFallbackVase();
      group.add(vase);
      console.log(`⚠️ Using fallback model for: ${itemName}`);
    } finally {
      // Always finish the loading operation
      if (loadingOperationId) {
        this.loadingManager?.finishLoading(loadingOperationId);
      }
    }
  }

  /**
   * Generic GLTF loader
   */
  async loadGLTFModel(url) {
    // Dynamic import of GLTFLoader to avoid build issues
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();

    // Detect incognito mode and memory constraints
    const isIncognito = await this.detectIncognitoMode();
    const isLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isIncognito) {
      console.log('🕵️ Incognito mode detected - configuring loader for blob-free loading');
      // Force cache clear before loading in incognito
      THREE.Cache.clear();
      if (window.gc) window.gc();
      
      // Configure loader to avoid blob URLs in incognito mode
      this.configureIncognitoLoader(loader);
    }

    // Add timeout to prevent hanging loads with proper cleanup
    let timeoutId = null;
    
    const loadPromise = new Promise((resolve, reject) => {
      loader.load(
        getFullUrl(url),
        (gltf) => {
          // Clear timeout on successful load
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          // Apply aggressive optimizations for incognito mode
          if (isIncognito || (isMobile && isLowMemory)) {
            this.optimizeForIncognito(gltf.scene);
          }
          
//          console.log('GLTF loaded successfully for URL:', getFullUrl(url));
          resolve(gltf);
        },
        (progress) => {
//          console.log('Loading progress for', getFullUrl(url), ':', progress.loaded, '/', progress.total);
        },
        (error) => {
          // Clear timeout on error
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          console.error('GLTF loader error for URL:', getFullUrl(url), error);
          reject(error);
        }
      );
    });

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Model load timeout')), 120000); // 120 second timeout for artwork models
    });

    try {
      return await Promise.race([loadPromise, timeoutPromise]);
    } finally {
      // Ensure timeout is always cleared
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }
  }

  /**
   * Create fallback vase geometry
   */
  createFallbackVase() {
    const vaseGeometry = new THREE.CylinderGeometry(0.2, 0.3, 0.6, 16);
    const vaseMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B4513,
      roughness: 0.8,
      metalness: 0.1
    });
    const vase = new THREE.Mesh(vaseGeometry, vaseMaterial);
    vase.position.y = 0; // Position relative to group
    vase.castShadow = false;
    vase.receiveShadow = true;
    vase.userData.isRotating = true;
    return vase;
  }

  /**
   * Configure GLTF loader to avoid memory issues in incognito mode
   */
  configureIncognitoLoader(loader) {
    console.log('🕵️ Configuring GLTF loader for incognito mode with reduced concurrency');
    
    // Create a loading manager with sequential loading for incognito
    const manager = new THREE.LoadingManager();
    
    // Track concurrent loads to prevent memory overload
    let activeLoads = 0;
    const maxConcurrentLoads = 2; // Reduce concurrent loads in incognito
    const pendingLoads = [];
    
    const originalItemCount = 0;
    const originalItemsLoaded = 0;
    
    manager.onStart = (url, itemsLoaded, itemsTotal) => {
      console.log(`🕵️ Starting incognito load: ${itemsLoaded}/${itemsTotal} - ${url}`);
    };
    
    manager.onLoad = () => {
      console.log('🕵️ All incognito resources loaded successfully');
      // Force cleanup after loading
      setTimeout(() => {
        if (window.gc) window.gc();
      }, 500);
    };
    
    manager.onError = (url) => {
      console.error('🕵️ Incognito load error:', url);
      activeLoads--;
      this.processNextLoad();
    };
    
    // Override URL processing to add delays between loads
    const originalSetURLModifier = manager.setURLModifier;
    manager.setURLModifier = (callback) => {
      return originalSetURLModifier.call(manager, (url) => {
        // Add artificial delay for blob URLs in incognito to prevent memory collisions
        if (url.startsWith('blob:')) {
          console.log('🕵️ Delaying blob URL load in incognito mode');
          return new Promise(resolve => {
            setTimeout(() => resolve(url), 100 + Math.random() * 200); // 100-300ms delay
          });
        }
        return callback ? callback(url) : url;
      });
    };
    
    loader.setLoadingManager(manager);
    
    console.log('🕵️ GLTF loader configured with reduced concurrency for incognito mode');
  }
  
  processNextLoad() {
    // Helper method for sequential loading
    if (this.pendingLoads && this.pendingLoads.length > 0 && this.activeLoads < 2) {
      const nextLoad = this.pendingLoads.shift();
      this.activeLoads++;
      nextLoad();
    }
  }

  /**
   * Aggressive optimizations for incognito mode
   */
  optimizeForIncognito(scene) {
    console.log('🕵️ Applying aggressive incognito optimizations...');
    
    scene.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        
        materials.forEach(material => {
          // Reduce texture quality for incognito mode only
          const textureProperties = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'];
          
          textureProperties.forEach(prop => {
            if (material[prop] && material[prop].isTexture) {
              const texture = material[prop];
              
              // More aggressive filtering for incognito
              texture.magFilter = THREE.LinearFilter;
              texture.minFilter = THREE.NearestFilter; // Even cheaper filtering
              texture.generateMipmaps = false;
              texture.flipY = false;
              
              // Force lower resolution only in incognito
              if (texture.image && texture.image.width > 512) {
                this.downscaleTexture(texture, 512);
              }
            }
          });
          
          // Simplify materials more aggressively
          if (material.isMeshStandardMaterial) {
            material.roughness = Math.max(0.5, material.roughness || 0.7);
            material.metalness = Math.min(0.3, material.metalness || 0.0);
            // Remove expensive maps in incognito
            if (material.normalMap) material.normalMap = null;
            if (material.roughnessMap) material.roughnessMap = null;
          }
          
          material.needsUpdate = true;
        });
        
        // Reduce geometry complexity for incognito
        if (child.geometry && child.geometry.attributes.position) {
          const positionCount = child.geometry.attributes.position.count;
          if (positionCount > 10000) {
            console.log(`🔧 High-poly mesh detected (${positionCount} vertices) - consider simplification`);
          }
        }
      }
    });
  }

  /**
   * Downscale texture to specific max size
   */
  downscaleTexture(texture, maxSize) {
    if (!texture.image || texture.image.width <= maxSize) return;
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const scale = maxSize / Math.max(texture.image.width, texture.image.height);
      canvas.width = Math.floor(texture.image.width * scale);
      canvas.height = Math.floor(texture.image.height * scale);
      
      ctx.drawImage(texture.image, 0, 0, canvas.width, canvas.height);
      
      // Create new image from canvas
      const newImage = new Image();
      newImage.onload = () => {
        texture.image = newImage;
        texture.needsUpdate = true;
        console.log(`🔧 Texture downscaled to ${canvas.width}x${canvas.height} for incognito mode`);
      };
      newImage.src = canvas.toDataURL();
      
    } catch (error) {
      console.warn('Failed to downscale texture for incognito mode:', error);
    }
  }

  /**
   * Detect incognito/private browsing mode
   */
  async detectIncognitoMode() {
    try {
      // Method 1: Storage quota (most reliable)
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        // Incognito typically has much lower quota (< 100MB vs several GB)
        return estimate.quota < 120000000; // Less than ~120MB suggests incognito
      }
      
      // Method 2: IndexedDB detection (fallback)
      return new Promise((resolve) => {
        const db = indexedDB.open('test');
        db.onerror = () => resolve(true); // Likely incognito if fails
        db.onsuccess = () => {
          resolve(false);
          indexedDB.deleteDatabase('test');
        };
      });
    } catch (e) {
      // If detection fails, assume worst case (incognito)
      return true;
    }
  }

  /**
   * Update rotating sculptures - DISABLED
   * Removed rotation animation as requested
   */
  updateRotations() {
    // Rotation animation removed - objects now use static rotation from placement data only
  }

  /**
   * Get all interactive objects
   */
  getInteractiveObjects() {
    return this.interactiveObjects;
  }

  /**
   * Clear all artworks
   */
  clearArtworks() {
    console.log(`🗑️ Clearing ${this.interactiveObjects.length} artworks from scene`);
    
    // Clear texture cache before disposing artworks
    THREE.Cache.clear();
    
    // Remove each artwork from scene and dispose properly
    this.interactiveObjects.forEach((obj, index) => {
      console.log(`🗑️ Removing artwork ${index + 1}: ${obj.name || 'unnamed'}`);
      
      // Remove from scene first
      if (obj.parent) {
        obj.parent.remove(obj);
      }
      
      // Then dispose
      this.disposeArtworkObject(obj);
    });
    
    // Clear the tracking array
    this.interactiveObjects = [];
    
    console.log('✅ All artworks cleared');
  }

  /**
   * Properly dispose of artwork objects
   */
  disposeArtworkObject(obj) {
    if (!obj) return;

    // Traverse and dispose all children
    obj.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(material => this.disposeMaterial(material));
        } else {
          this.disposeMaterial(child.material);
        }
      }
    });

    // Remove from scene
    if (obj.parent) {
      obj.parent.remove(obj);
    }
  }

  /**
   * Dispose of a material properly
   */
  disposeMaterial(material) {
    if (!material) return;

    // Dispose all textures in the material
    Object.keys(material).forEach(key => {
      const value = material[key];
      if (value && typeof value === 'object' && value.isTexture) {
        value.dispose();
      }
    });

    // Dispose the material itself
    material.dispose();
  }
}